import React from 'react';
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  return (
    <footer id="contact" className="bg-[#1A1A1A] text-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <MapPin className="w-5 h-5 mr-3 mt-0.5 text-[#CC0000]" />
                <span>Dawki Mobility Headquarters<br />
                Plot No. IP- 20, Gat No. 342, Khed City,<br />
                Village - Nimgaon, Tal. - Khed,<br />
                Dist. - Pune (M.S.) Pin Code - 410505, India</span>
              </li>
              <li className="flex items-center">
                <Phone className="w-5 h-5 mr-3 text-[#CC0000]" />
                <a 
                  href="tel:+919028519202"
                  className="text-white hover:text-[#CC0000] transition-colors duration-200 underline decoration-transparent hover:decoration-[#CC0000]"
                  title="Call +91 90285 19202"
                  onClick={(e) => {
                    // Enhanced fallback for desktop users
                    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                    
                    if (!isMobile) {
                      // For desktop, try multiple fallback options
                      const phoneNumber = '+919028519202';
                      const skypeUrl = `skype:${phoneNumber}?call`;
                      const whatsappUrl = `https://wa.me/${phoneNumber}`;
                      
                      // Try Skype first, then WhatsApp Web as fallback
                      const trySkype = () => {
                        const skypeWindow = window.open(skypeUrl, '_blank');
                        if (!skypeWindow || skypeWindow.closed || typeof skypeWindow.closed === 'undefined') {
                          // If Skype fails, try WhatsApp Web
                          setTimeout(() => {
                            const whatsappWindow = window.open(whatsappUrl, '_blank');
                            if (!whatsappWindow || whatsappWindow.closed || typeof whatsappWindow.closed === 'undefined') {
                              // If both fail, show a helpful message
                              alert(`Please call us at: ${phoneNumber}\n\nOr use Skype/WhatsApp to contact us.`);
                            }
                          }, 1000);
                        }
                      };
                      
                      // Small delay to allow tel: protocol to work first
                      setTimeout(trySkype, 500);
                    }
                    // For mobile devices, tel: protocol will work automatically
                  }}
                >
                  +91 90285 19202
                </a>
              </li>
              <li className="flex items-center">
                <Mail className="w-5 h-5 mr-3 text-[#CC0000]" />
                <a 
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=sales@3dmagicindia.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#CC0000] transition-colors duration-200 underline decoration-transparent hover:decoration-[#CC0000]"
                  title="Send email to sales@3dmagicindia.com"
                  onClick={(e) => {
                    // Fallback for non-Gmail users
                    const fallbackMailto = 'mailto:sales@3dmagicindia.com';
                    
                    // Try to open Gmail first
                    const gmailUrl = 'https://mail.google.com/mail/?view=cm&fs=1&to=sales@3dmagicindia.com';
                    const newWindow = window.open(gmailUrl, '_blank');
                    
                    // If popup was blocked or failed, use mailto as fallback
                    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                      e.preventDefault();
                      window.location.href = fallbackMailto;
                    }
                  }}
                >
                  sales@3dmagicindia.com
                </a>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/passenger-vehicles"
                  className="hover:text-[#CC0000] transition-colors duration-200"
                >
                  Vehicles
                </Link>
              </li>
              <li>
                <Link 
                  to="/mission"
                  className="hover:text-[#CC0000] transition-colors duration-200"
                >
                  About Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Business Hours</h3>
            <ul className="space-y-2">
              <li><span className="font-medium">Mon-Fri:</span> 9:00 AM - 6:00 PM</li>
              <li><span className="font-medium">Saturday:</span> 10:00 AM - 4:00 PM</li>
              <li><span className="font-medium">Sunday:</span> Closed</li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/share/1Cdn32989S/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-[#CC0000] p-2 rounded-full transition-colors duration-200"
                aria-label="Follow us on Facebook"
                title="Visit our Facebook page"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/dawkimobility/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-[#CC0000] p-2 rounded-full transition-colors duration-200"
                aria-label="Follow us on Instagram"
                title="Visit our Instagram page"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/dawki-mobility/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-[#CC0000] p-2 rounded-full transition-colors duration-200"
                aria-label="Follow us on LinkedIn"
                title="Visit our LinkedIn page"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center">
          <p>&copy; {new Date().getFullYear()} Dawki Mobility. All rights reserved.</p>
          <div className="mt-4 md:mt-0 flex space-x-6">
            <a href="#" className="text-sm hover:text-[#CC0000] transition-colors duration-200">Privacy Policy</a>
            <a href="#" className="text-sm hover:text-[#CC0000] transition-colors duration-200">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;